import type { ButtonHTMLAttributes, CSSProperties } from "react";
import { BUTTON_RESET, pressClass } from "./press";

export interface FillPillProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** The action's copy — always `t('…')`, never a literal. */
  label: string;
  onClick: () => void;
  /**
   * `submit` where the pill closes a `<form>` — onboarding's steps are
   * forms, and the keyboard's "go" has to work. @default "button"
   */
  type?: "button" | "submit";
}

/* 05-SHARED.md: height 48, radius 999, `--d-fill` on `--d-on-fill`, --t-ui,
 * centred on both axes. No shadow — v2 has none anywhere. */
const BASE: CSSProperties = {
  ...BUTTON_RESET,
  height: "var(--d-btn-h)",
  borderRadius: "var(--r-pill)",
  background: "var(--d-fill)",
  color: "var(--d-on-fill)",
  font: "var(--t-ui)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
};

/* 01-ONBOARDING.md §2: an empty name leaves "Next" inactive — «не показывать
 * красное: ошибка это копирайт, не цвет». So the pill keeps its geometry and
 * its `--d-fill`, and only drops to the alpha the system already uses for a
 * dimmed thing (`--d-fg-3` is `.52`). The press class comes off with it —
 * that is what carries `cursor: pointer` and the `:active` scale. */
const DISABLED: CSSProperties = { opacity: 0.52, cursor: "default" };

/**
 * The one opaque action a screen is allowed. Everything else is a `Pill`, a
 * `TxtLink` or a `Row`; **at most one `FillPill` per screen** — a rule the
 * screen owns, since an atom cannot see its siblings.
 *
 * Ported from `FillPill` in
 * `docs/design/handoff/design/templates/you/you-screen.jsx`.
 */
export function FillPill({
  label,
  onClick,
  type = "button",
  disabled = false,
  className,
  style,
  ...rest
}: FillPillProps): JSX.Element {
  return (
    <button
      type={type}
      disabled={disabled}
      className={disabled ? className : pressClass(className)}
      onClick={onClick}
      style={{ ...BASE, ...(disabled ? DISABLED : null), ...style }}
      {...rest}
    >
      {label}
    </button>
  );
}

export default FillPill;
